/**
 * x-product-card directive.
 *
 * Handles variant-picker events on a product card. When a variant is hovered
 * (intent), the card prefetches the variant URL for faster subsequent loads.
 * When a variant is selected and the section HTML is updated, the directive
 * morphs the card body in place to reflect the new variant data.
 *
 * @options
 *   selector       - CSS selector for the card body element to morph on variant update
 *                    (default: ".resource-card-body").
 *   morphSelectors - Additional selectors passed to `$morph` to constrain which
 *                    child elements are updated during the morph operation (default: []).
 *
 * @example
 *   <div x-product-card="{ selector: '.resource-card-body' }">…</div>
 */
(() => {
  const root = (
    Alpine,
    el,
    { selector = ".resource-card-body", morphSelectors = [] },
  ) => {
    Alpine.bind(el, () => {
      return {
        /**
         * Prefetches the variant URL when the user hovers over a variant option.
         * @param {CustomEvent} event - The `variant-picker-intent` event containing `intentProductVariantUrl`.
         */
        ["@variant-picker-intent.stop"](event) {
          const { intentProductVariantUrl } = event.detail;

          this.$app.$prefetchManager.prefetch(intentProductVariantUrl);
        },

        /**
         * Morphs the card body with updated variant HTML when a variant is selected.
         * @param {CustomEvent} event - The `variant-picker-updated` event containing `html`.
         */
        ["@variant-picker-updated.stop"](event) {
          const { html } = event.detail;

          const existingElement = this.$el.querySelector(selector);
          const newElement = html.querySelector(selector);

          this.$morph(existingElement, newElement, {
            morphSelectors,
          });
        },
      };
    });
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("product-card", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};
      root(Alpine, el, options);
    }).before("bind");
  });
})();
